import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from 'react';
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MiniMap,
  Panel,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

import Save from '@mui/icons-material/Save';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Box, Paper, Divider } from '@mui/material';
import { useLocalization } from '@ui-framework/index';
import {
  getLayoutedElements,
  parseFlowData,
  normalizeEdgesForDraggable,
  applyEdgeStyles,
  applyEdgesToNodeRoutes,
} from './utils/flowUtils';
import CustomNode from './components/CustomNode';
import PropertiesPanel from './components/PropertiesPanel/index';
import DraggableEdge from './components/DraggableEdge';
import Sidebar from './components/Sidebar';
import ToolbarButton from './components/ToolbarButton';
import FlowGlobalStyles from './components/FlowGlobalStyles';
import WIDGET_TEMPLATES from './WidgetTemplates';

const nodeTypes = { custom: CustomNode };
const edgeTypes = { draggable: DraggableEdge };

function FlowDesigner({ data, onSubmit }) {
  const { definitionJson } = data;
  const { formatMessage } = useLocalization();
  const [fullscreen, setFullscreen] = useState(false);
  const reactFlowWrapper = useRef(null);
  const { zoomIn, zoomOut } = useReactFlow();
  const [locked, setLocked] = useState(false);
  const [zoom, setZoom] = useState(1);
  const minZoom = 0.1;
  const maxZoom = 2;

  const handleFullscreen = () => {
    const el = reactFlowWrapper.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  // Sync fullscreen state when user exits via Esc or browser UI
  useEffect(() => {
    const el = reactFlowWrapper.current;
    if (!el) return undefined;
    const onFullscreenChange = () => {
      setFullscreen(document.fullscreenElement === el);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const { nodes: parsedNodes, edges: parsedEdges } =
    parseFlowData(definitionJson);

  const hasExplicitPositions = parsedNodes.some(
    n => n.data.original.ui?.position,
  );
  const layouted = hasExplicitPositions
    ? { nodes: parsedNodes, edges: parsedEdges }
    : getLayoutedElements(parsedNodes, parsedEdges);

  const initialState = useMemo(
    () => ({ nodes: layouted.nodes, edges: layouted.edges }),
    [layouted.edges, layouted.nodes],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialState.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialState.edges);
  const [selectedNode, setSelectedNode] = useState(null);

  // Auto-close PropertiesPanel if selected node is deleted
  useEffect(() => {
    if (selectedNode && !nodes.some(n => n.id === selectedNode.id)) {
      setSelectedNode(null);
    }
  }, [nodes, selectedNode]);

  const { screenToFlowPosition } = useReactFlow();

  useEffect(() => {
    setEdges(eds => normalizeEdgesForDraggable(eds));
  }, [setEdges]);

  const onNodeClick = useCallback(
    (_event, node) => {
      setSelectedNode(node);
      setEdges(eds => applyEdgeStyles(eds, { highlightedNodeId: node.id }));
    },
    [setEdges],
  );

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setEdges(eds => applyEdgeStyles(eds, { highlightedNodeId: null }));
  }, [setEdges]);

  const onNodeUpdate = useCallback(
    updatedNode => {
      const updatedNodes = nodes.map(n =>
        n.id === updatedNode.id ? updatedNode : n,
      );
      const flowData = {
        nodes: updatedNodes.map(n => n.data.original),
      };
      const { edges: newEdges } = parseFlowData(flowData);
      const styledEdges = newEdges.map(e => ({
        ...e,
        type: 'smoothstep',
        markerEnd: { type: MarkerType.ArrowClosed },
      }));

      setNodes(updatedNodes);
      setEdges(styledEdges);
      setSelectedNode(updatedNode);
    },
    [nodes, setNodes, setEdges],
  );

  const onConnect = useCallback(
    params => {
      setEdges(eds => {
        const newEdges = eds.filter(
          e =>
            !(
              e.source === params.source &&
              e.sourceHandle === params.sourceHandle
            ),
        );
        const withNewEdge = addEdge(
          {
            ...params,
            type: 'draggable',
            markerEnd: { type: MarkerType.ArrowClosed },
            markerMid: { type: MarkerType.ArrowClosed },
          },
          newEdges,
        );
        return normalizeEdgesForDraggable(withNewEdge);
      });
    },
    [setEdges],
  );

  const onSave = () => {
    const nodesWithPosition = nodes.map(n => ({
      ...n.data.original,
      ui: { position: n.position },
    }));
    const nodesWithRoutes = applyEdgesToNodeRoutes(nodesWithPosition, edges);
    const flowData = {
      id: definitionJson.id || 'flow_definition',
      nodes: nodesWithRoutes,
      start: definitionJson.start || 'trigger',
    };
    onSubmit({
      name: data.name,
      active: data.active,
      definitionJson: flowData,
    });
  };

  const onDragOver = useCallback(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    e => {
      e.preventDefault();
      const type = e.dataTransfer.getData('application/reactflow/type');
      if (typeof type === 'undefined' || !type) return;
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const newId = `${type.toLowerCase()}_${nodes.length + 1}`;
      // Deep clone the template to avoid shared reference bugs
      const template = WIDGET_TEMPLATES[type];
      const original = template
        ? JSON.parse(JSON.stringify({ ...template, id: newId }))
        : { id: newId, type, attributes: {}, routes: [] };
      const onDelete = () => {
        setNodes(nds => nds.filter(n => n.id !== newId));
        setSelectedNode(null);
      };
      const newData = {
        label: newId,
        nodeType: type,
        original,
        onDelete,
      };
      const newNode = { id: newId, type: 'custom', position, data: newData };
      setNodes(nds => nds.concat(newNode));
      setSelectedNode(newNode);
    },
    [nodes, setNodes, screenToFlowPosition],
  );

  const nodesWithDelete = nodes.map(node =>
    node.type === 'custom'
      ? {
          ...node,
          data: {
            ...node.data,
            onDelete: () => {
              setNodes(nds => nds.filter(n => n.id !== node.id));
              setSelectedNode(null);
            },
          },
        }
      : node,
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: fullscreen ? '100vh' : '80vh',
        bgcolor: 'grey.50',
        position: 'relative',
        zIndex: fullscreen ? 1300 : 'auto',
      }}
      ref={reactFlowWrapper}>
      <FlowGlobalStyles />
      <ReactFlow
        nodes={nodesWithDelete}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        minZoom={minZoom}
        maxZoom={maxZoom}
        onMove={(_evt, viewport) => setZoom(viewport.zoom)}
        onDragOver={onDragOver}
        onDrop={onDrop}
        attributionPosition="bottom-left"
        nodesDraggable={!locked}
        nodesConnectable={!locked}
        elementsSelectable={!locked}>
        <Background gap={12} size={1} />
        <MiniMap nodeStrokeWidth={3} zoomable pannable position="bottom-left" />

        <Panel position="top-center">
          <Paper
            elevation={1}
            sx={{
              p: 0.5,
              borderRadius: 1,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}>
            <ToolbarButton
              icon={<ZoomIn sx={{ fontSize: 20 }} />}
              label={formatMessage({
                id: 'FLOW_TOOLBAR_ZOOM_IN',
                defaultMessage: 'Zoom In',
              })}
              onClick={zoomIn}
              disabled={zoom >= maxZoom}
              tooltipTitle={formatMessage({
                id: 'FLOW_TOOLBAR_ZOOM_IN',
                defaultMessage: 'Zoom In',
              })}
            />
            <ToolbarButton
              icon={<ZoomOut sx={{ fontSize: 20 }} />}
              label={formatMessage({
                id: 'FLOW_TOOLBAR_ZOOM_OUT',
                defaultMessage: 'Zoom Out',
              })}
              onClick={zoomOut}
              disabled={zoom <= minZoom}
              tooltipTitle={formatMessage({
                id: 'FLOW_TOOLBAR_ZOOM_OUT',
                defaultMessage: 'Zoom Out',
              })}
            />
            <ToolbarButton
              icon={
                locked ? (
                  <LockIcon sx={{ fontSize: 20 }} />
                ) : (
                  <LockOpenIcon sx={{ fontSize: 20 }} />
                )
              }
              label={formatMessage({
                id: locked ? 'FLOW_TOOLBAR_LOCKED' : 'FLOW_TOOLBAR_UNLOCKED',
                defaultMessage: locked ? 'Locked' : 'Unlocked',
              })}
              onClick={() => setLocked(l => !l)}
              tooltipTitle={formatMessage({
                id: 'FLOW_TOOLBAR_LOCK_UNLOCK',
                defaultMessage: 'Lock/Unlock Pan & Zoom',
              })}
            />
            <Divider orientation="vertical" flexItem />
            <ToolbarButton
              icon={
                fullscreen ? (
                  <FullscreenExit sx={{ fontSize: 20 }} />
                ) : (
                  <Fullscreen sx={{ fontSize: 20 }} />
                )
              }
              label={formatMessage({
                id: fullscreen
                  ? 'FLOW_TOOLBAR_EXIT_FULLSCREEN'
                  : 'FLOW_TOOLBAR_FULLSCREEN',
                defaultMessage: fullscreen ? 'Exit fullscreen' : 'Fullscreen',
              })}
              onClick={handleFullscreen}
              tooltipTitle={formatMessage({
                id: fullscreen
                  ? 'FLOW_TOOLBAR_EXIT_FULLSCREEN_TOOLTIP'
                  : 'FLOW_TOOLBAR_FULLSCREEN_TOOLTIP',
                defaultMessage: fullscreen
                  ? 'Exit fullscreen (or press Esc)'
                  : 'Fullscreen (Press Esc to exit)',
              })}
            />
            <Divider orientation="vertical" flexItem />
            <ToolbarButton
              icon={<Save sx={{ fontSize: 20 }} />}
              label={formatMessage({
                id: 'FLOW_TOOLBAR_SAVE',
                defaultMessage: 'Save',
              })}
              onClick={onSave}
              tooltipTitle={formatMessage({
                id: 'FLOW_TOOLBAR_SAVE_TOOLTIP',
                defaultMessage: 'Save flow definition',
              })}
            />
          </Paper>
        </Panel>
      </ReactFlow>

      {selectedNode ? (
        <PropertiesPanel
          selectedNode={selectedNode}
          nodes={nodes}
          onUpdate={onNodeUpdate}
          onClose={() => onPaneClick()}
        />
      ) : (
        <Sidebar />
      )}
    </Box>
  );
}

function DefinitionFlow({ data, onSubmit }) {
  return (
    <ReactFlowProvider>
      <FlowDesigner data={data} onSubmit={onSubmit} />
    </ReactFlowProvider>
  );
}

export default DefinitionFlow;
